import Dexie from 'dexie';
import moment from 'moment';
import {getUsername} from '../../_shared/GWTBridge/invoke/genPage';
import {isPersonalizationSource} from '../../_shared/GWTBridge/invoke/netForm';

import {
    getNeverShowAgainByForm,
    getNeverShowAgainServer,
    checkClearStorage
} from '../../features/Personalization/actions';
import {
    getLastCollectionDates,
    startNewCollection,
    getPrevCollectionDate
} from '../../features/Personalization/service/indexDBService';
import {configuredDate} from '../../features/Personalization/utils/personalization';
import {configuration as baseConfiguration} from '../../features/Personalization/config';

interface DataItem {
    date: string;
    count: number;
}
interface Action {
    formName: string;
    name: string;
    title: string;
    user: string;
    collectionDate?: string;
    data?: DataItem[];
    total?: number;
    type?: string;
    exec?: number;
    isButton?: boolean;
}

interface CollectionDate {
    date: string;
    id?: number;
    neverShowAgain: boolean;
    formName: string;
    user: string;
}

interface BlackList {
    name: string;
    user: string;
}

class PersonalizationActions extends Dexie {
    public actions: Dexie.Table<Action, string>;
    public collectionDates: Dexie.Table<CollectionDate, number>;
    public blackList: Dexie.Table<BlackList, number>;

    constructor() {
        super('PersonalizationActions');
        this.version(1).stores({
            actions: '[formName+collectionDate+user+name],[collectionDate+user]',
            collectionDates: '[formName+date+user], [formName+user], user',
            blackList: 'user, name'
        });
    }
}

export const db = new PersonalizationActions();

export const configuration = {
    ...baseConfiguration,
    minActionsEntries: 3,
    neverFormKey: 'actionsNeverShowSubForm',
    neverAllKey: 'actionsNeverShowSubFormsSugg'
};

export const addActionsEntry = async (action: Action) => {
    db.transaction('rw', db.actions, db.collectionDates, db.blackList, async () => {
        const userName = getUsername();

        const blackList = await db.blackList.where('user').equals(userName).toArray();

        if (blackList.some(blItem => blItem.name === action.name)) {
            return;
        }

        const lastCollection = await getLastCollectionDates(db, action.formName);
        const collectionDate =
            lastCollection?.date || (await startNewCollection(db, action.formName, configuration));

        if (!lastCollection?.neverShowAgain) {
            const today = configuredDate().format('DD/MM/YYYY');

            const entityToUpdate = await db.actions.get({
                formName: action.formName,
                name: action.name,
                collectionDate,
                user: userName
            });
            if (entityToUpdate) {
                db.actions
                    .where('[formName+collectionDate+user+name]')
                    .equals([
                        entityToUpdate.formName,
                        collectionDate,
                        entityToUpdate.user,
                        entityToUpdate.name
                    ])
                    .modify(ent => {
                        const todayEntryIndex = ent.data.findIndex(data => data.date === today);
                        if (todayEntryIndex > -1) {
                            ent.data[todayEntryIndex].count = ent.data[todayEntryIndex].count + 1;
                        } else {
                            ent.data.push({date: today, count: 1});
                        }
                    });
            } else {
                db.actions.add({
                    ...action,
                    collectionDate,
                    user: userName,
                    data: [{date: today, count: 1}]
                });
            }
        }
    }).catch(e => {
        console.log('error adding new entry  entitys', e);
    });
};

const cache: any = {};

export const getActionSuggestion = async (formName: string, items, isSnoozed: boolean) => {
    if (isPersonalizationSource()) {
        let lastCollectionDate, shouldExecute;
        let actionsMonthly = [];
        let neverShowAgain = false;

        checkClearStorage();

        const isNeverShowAgainForm = await getNeverShowAgainByForm(
            formName,
            configuration.neverFormKey
        );
        const isNeverShowAgainAll = await getNeverShowAgainServer(configuration.neverAllKey);

        neverShowAgain = isNeverShowAgainAll || isNeverShowAgainForm;

        if (!neverShowAgain) {
            await db
                .transaction('r', db.collectionDates, db.actions, db.blackList, async () => {
                    //check parent form in black list
                    const userName = getUsername();

                    const blackList = await db.blackList.where('user').equals(userName).toArray();

                    if (blackList?.some(blItem => blItem.name === formName)) {
                        return;
                    }

                    const collectionDate = isSnoozed
                        ? await getPrevCollectionDate(db, formName)
                        : await getLastCollectionDates(db, formName);

                    neverShowAgain = collectionDate?.neverShowAgain;
                    if (!collectionDate || neverShowAgain) {
                        return;
                    }

                    lastCollectionDate = moment(collectionDate?.date, 'DD/MM/YYYY');
                    const daysDiff = configuredDate().diff(lastCollectionDate, 'days');

                    shouldExecute = isSnoozed || daysDiff >= configuration.days;

                    if (shouldExecute) {
                        const userName = getUsername();
                        actionsMonthly = await db.actions
                            .where('[formName+collectionDate+user]')
                            .equals([formName, collectionDate.date, userName])
                            .toArray();
                    }
                })
                .catch(e => console.log('get data from indexedDB error: ', e));

            if (actionsMonthly.length > 0 && !neverShowAgain && shouldExecute) {
                if (isSnoozed && cache[formName]) {
                    return cache[formName];
                }

                const actions = calcEntitiesToAdd(actionsMonthly, items, isSnoozed);
                if (isSnoozed) {
                    cache[formName] = actions;
                }

                return actions;
            }
        }
    }
    return [];
};

const isShownAsButton = (action: Action, items, isSnoozed: boolean): boolean => {
    const eName = isSnoozed ? 'ename' : 'eName';
    return action?.name === items?.[0]?.[eName] || action?.name === items?.[1]?.[eName];
};

const calcEntitiesToAdd = (actions: Action[], items, isSnoozed): Action[] => {
    const actionItems = [];
    let keepButton = 0;
    // 1. add total and is button to actions array
    // 2. sort actions from most used to least used
    // 3. filter actions below minimum
    actions = actions
        ?.map((item: Action) => {
            return {
                ...item,
                total: item.data.reduce((total, data) => data.count + total, 0),
                isButton: isShownAsButton(item, items, isSnoozed)
            };
        })
        .sort((a: Action, b: Action) => b.total - a.total)
        .filter((item: Action) => item.total >= configuration.minActionsEntries)
        .slice(0, 2);

    const [topAction, secondAction] = actions;
    const [, secondItem] = items;

    if (topAction?.isButton && topAction?.name === secondItem?.eName) keepButton = 1;
    else if (!topAction?.isButton && secondAction?.name === secondItem.eName) {
        keepButton = 1;
    }

    actions
        .filter((item: Action) => !item.isButton)
        .forEach((action: Action) => {
            items.forEach(item => {
                const isSameAction = isSnoozed
                    ? action.name === item.ename
                    : action.name === item.eName;
                if (isSameAction) {
                    actionItems.push({
                        ...action,
                        type: item.type,
                        exec: item.exec,
                        title: item.title,
                        keepButton
                    });
                }
            });
        });

    return actionItems;
};
